import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Users, Minus, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface BookingDetails {
  checkIn: string | null;
  checkOut: string | null;
  adults: number;
  children: number;
}

interface BookingDetailsFormProps {
  value: BookingDetails;
  onChange: (details: BookingDetails) => void;
}

const Counter = ({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors disabled:opacity-40"
        disabled={value <= min}
      >
        <Minus className="w-3 h-3" />
      </button>
      <span className="text-sm font-medium w-5 text-center text-foreground">{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  </div>
);

const BookingDetailsForm = ({ value, onChange }: BookingDetailsFormProps) => {
  const checkInDate = value.checkIn ? new Date(value.checkIn + "T00:00:00") : undefined;
  const checkOutDate = value.checkOut ? new Date(value.checkOut + "T00:00:00") : undefined;

  const handleCheckIn = (date: Date | undefined) => {
    onChange({ ...value, checkIn: date ? format(date, "yyyy-MM-dd") : null });
  };

  const handleCheckOut = (date: Date | undefined) => {
    onChange({ ...value, checkOut: date ? format(date, "yyyy-MM-dd") : null });
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className="rounded-xl border bg-card p-4 space-y-4">
        <p className="text-xs text-muted-foreground font-medium">
          We couldn't detect trip details from your URL. Add them below (optional):
        </p>

        <div className="grid grid-cols-2 gap-3">
          {/* Check-in */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left text-sm font-normal h-10",
                  !checkInDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {checkInDate ? format(checkInDate, "MMM d, yyyy") : "Check-in"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkInDate}
                onSelect={handleCheckIn}
                disabled={(date) => date < new Date()}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>

          {/* Check-out */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left text-sm font-normal h-10",
                  !checkOutDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {checkOutDate ? format(checkOutDate, "MMM d, yyyy") : "Check-out"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkOutDate}
                onSelect={handleCheckOut}
                disabled={(date) =>
                  date < new Date() || (checkInDate ? date <= checkInDate : false)
                }
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-3">
          <Counter
            label="Adults"
            value={value.adults}
            onChange={(v) => onChange({ ...value, adults: v })}
            min={1}
          />
          <Counter
            label="Children"
            value={value.children}
            onChange={(v) => onChange({ ...value, children: v })}
            min={0}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default BookingDetailsForm;
